/**
 * @fileoverview Renders pagination info (start, end, numElements), buttons for controlling
 * pagination, and a select for limiting amount of info displayed.
 *
 * An example of the page info is: 1 - 10 of 25.
 *
 * This component supports offset-based pagination, and will notify the parent's handler of
 * changes. Internally, the state of {offset} is 0-based, but is displayed as 1-based.
 *
 * If {onOffsetLimitChange} is an async function, a loading dialog should be shown before the
 * async function is called to prevent unnecessary fetches. The first parameter is the OFFSET
 * and the second is the LIMIT.
 *
 * @author Henry Wang (hwang@yext.com)
 */

goog.declareModuleId('yext.ui.components.PaginationControls');

import {Button} from '/ui/components/button/button';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';
import {Column} from '/ui/components/layout/column';
import {Row} from '/ui/components/layout/row';
import {SelectController} from '/ui/components/select/selectcontroller';
import {hasManaUITheme} from '/ui/design/hasManaUITheme';
import {yext} from '/ui/lib/msg';

import * as styles from '/ui/components/pagination/paginationcontrols.module.scss';

/**
 * @typedef {{
 *   limit: number,
 *   offset: number,
 *   total: (number|null|undefined),
 * }}
 */
export let PaginationDataShape;

export const DEFAULT_LIMIT_OPTIONS = [
  {
    value: 25,
    label: yext.msg('25'),
  },
  {
    value: 50,
    label: yext.msg('50'),
  },
  {
    value: 100,
    label: yext.msg('100'),
  },
];

export const SMALL_LIMIT_OPTIONS = [
  {
    value: 5,
    label: yext.msg('5'),
  },
  {
    value: 10,
    label: yext.msg('10'),
  },
  {
    value: 25,
    label: yext.msg('25'),
  },
];

/**
 * Calculates the end position given an offset
 * @param {number} offset
 * @param {number} showLimit
 * @param {number} numElements
 * @return {number}
 */
const getEnd = (offset, showLimit, numElements) => {
  return Math.min(offset + showLimit, numElements);
};

/**
 * @param {{disabled: boolean, onClick?: (function():void)}} props
 * @return {React.ReactElement}
 */
export const NextButton = ({disabled, onClick}) => {
  return (
    <div className={'yext__pagination--next ' + styles.paginationButton}>
      <Button
        disabled={disabled}
        onClick={onClick}
        ariaLabel={yext.msg('Next Page')}
      >
        <i className={disabled ? 'icon-arrow-right-gray4' : 'icon-arrow-right'}></i>
      </Button>
    </div>
  );
};

/**
 * @param {{disabled: boolean, onClick?: (function():void)}} props
 * @return {React.ReactElement}
 */
export const PreviousButton = ({disabled, onClick}) => {
  return (
    <div className={'yext__pagination--prev ' + styles.paginationButton}>
      <Button
        disabled={disabled}
        onClick={onClick}
        ariaLabel={yext.msg('Previous Page')}
      >
        <i className={disabled ? 'icon-arrow-left-gray4' : 'icon-arrow-left'}></i>
      </Button>
    </div>
  );
};

/**
 * @typedef {{
 *   paginationData: !PaginationDataShape,
 *   onOffsetLimitChange: (function(number, number):void),
 *   limitOptions?: ?,
 *   hideLimitSelect?: boolean,
 *   tid?: string,
 *   loading?: boolean,
 * }}
 */
export let PaginationControlsProps;

/**
 * @param {PaginationControlsProps} props
 * @return {React.ReactElement}
 */
export const PaginationControls = function(props) {
  const {
    paginationData,
    onOffsetLimitChange,
    limitOptions,
    hideLimitSelect,
    tid,
    loading = false,
  } = props;
  const {limit, offset} = paginationData;
  let total = /** @type {number} */ (paginationData.total);
  if (total === undefined || total === null) {
    total = Infinity;
  }

  const handlePagePrev = () => {
    onOffsetLimitChange(Math.max(0, offset - limit), limit);
  };

  const handlePageNext = () => {
    onOffsetLimitChange(Math.max(0, offset + limit), limit);
  };

  /**
   * Called when changing show
   * @param {number} newLimit
   */
  const handleOnShowLimitSelect = newLimit => {
    if (newLimit === limit) {
      return;
    }
    if (newLimit >= total) {
      onOffsetLimitChange(0, newLimit);
      return;
    }
    const end = getEnd(offset, limit, total);
    if (newLimit > limit && end - newLimit >= 0) {
      onOffsetLimitChange(end - newLimit, newLimit);
      return;
    }
    onOffsetLimitChange(offset, newLimit);
  };

  /**
   * Handles rendering of the page info in this format: 1-10 of 25, where digits are bolded.
   * @return {React.ReactElement}
   */
  const renderPaginationInfo = () => {
    if (loading) {
      return (
        <div className={`${styles.infoLoading} yext__pagination__info__counts yext__pagination__item`}>
          <ContentSkeleton/>
        </div>
      );
    }
    let start = 0;
    let end = 0;
    if (total > 0) {
      start = offset + 1;
      end = getEnd(offset, limit, total);
    }
    const paginationContents = yext.msgc(
      'Pagination control. Ex: 1-10 of 25',
      '<b>{0}</b>-<b>{1}</b> of <b>{2}</b>',
      start.toLocaleString(),
      end.toLocaleString(),
      total === Infinity ? yext.msg('Many') : total.toLocaleString(),
    );

    return (
      <div className={'yext__pagination__info__counts yext__pagination__item'}>
        <span dangerouslySetInnerHTML={{__html: paginationContents}}/>
      </div>
    );
  };

  /**
   * Handles rendering of the pagination buttons.
   * @return {React.ReactElement}
   */
  const renderPaginationButtons = () => {
    const defaultRender = <>
      {renderPaginationInfo()}
      <div className={'yext__pagination__controls yext__pagination__item'}>
        <PreviousButton
          disabled={loading || offset === 0}
          onClick={handlePagePrev}
        />
        <NextButton
          disabled={loading || offset + limit >= total}
          onClick={handlePageNext}
        />
      </div>
    </>;

    const manaStyle =
      <div className={'yext__pagination__controls yext__pagination__item'}>
        <PreviousButton
          disabled={loading || offset === 0}
          onClick={handlePagePrev}
        />
        {renderPaginationInfo()}
        <NextButton
          disabled={loading || offset + limit >= total}
          onClick={handlePageNext}
        />
      </div>;

    return hasManaUITheme() ? manaStyle : defaultRender;
  };

  return (
    <Row rowClasses={['yext__pagination']}>
      <Column size={'fullWidth'} columnClasses={['yext__pagination']}>
        <div tid={tid}>
          {!hideLimitSelect && (
            <>
              <div className={'yext__pagination__info yext__pagination__item'}>
                <span>{yext.msg('Show')}</span>
              </div>
              <div className={'yext__pagination__controls yext__pagination__item'}>
                <div className={'yext__pagination__controls__select'}>
                  <SelectController
                    config={{
                      disabled: loading,
                      name: 'basic-short',
                      onSelect: handleOnShowLimitSelect,
                    }}
                    options={limitOptions}
                    selectedOptions={[limit]}
                  />
                </div>
              </div>
            </>
          )}
          {renderPaginationButtons()}
        </div>
      </Column>
    </Row>
  );
};

PaginationControls.defaultProps = {
  limitOptions: DEFAULT_LIMIT_OPTIONS,
  pageNumber: 1,
  hideLimitSelect: false,
};

PaginationControls['defaultProps'] = PaginationControls.defaultProps;
